import { useState } from "react";
import { useAuthContext } from "../context/AuthContext";
import { supabase } from "../lib/supabase";
import { fetchUser } from "../api/UserApi";
import { AuthUser, UseLoginResult } from "../types/auth";
import { captureException, identifyUser } from "../lib/posthog";

export const useLogin = (): UseLoginResult => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [otpSent, setOtpSent] = useState<boolean>(false);
  const { dispatch } = useAuthContext();

  const requestOTP = async (email: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    setOtpSent(false);

    try {
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email,
        options: { shouldCreateUser: false },
      });

      if (otpError) {
        if (
          otpError.message.includes("User not found") ||
          otpError.message.includes("Signups not allowed") ||
          otpError.status === 400
        ) {
          setError("Ingen användare hittades med denna e-post");
        } else {
          setError("Kunde inte skicka verifieringskod");
        }
        setIsLoading(false);
        return false;
      }

      setOtpSent(true);
      setIsLoading(false);
      return true;
    } catch (err) {
      captureException(err, { source: "useLogin", step: "requestOTP", email });
      setError("Kunde inte skicka verifieringskod");
      setIsLoading(false);
      return false;
    }
  };

  const verifyOTP = async (
    email: string,
    token: string
  ): Promise<AuthUser | undefined> => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        email,
        token,
        type: "email",
      });

      if (verifyError || !data.session || !data.user) {
        if (verifyError?.message.includes("expired")) {
          setError("Verifieringskoden har gått ut. Begär en ny kod.");
        } else if (verifyError?.message.includes("invalid")) {
          setError("Ogiltig verifieringskod. Försök igen.");
        } else {
          setError("Verifiering misslyckades");
        }
        setIsLoading(false);
        return undefined;
      }

      const user = data.user;
      const session = data.session;

      try {
        const userDetailsResponse = await fetchUser({
          queryKey: [null, user.id],
        });
        const userDetails = userDetailsResponse.data;

        const role = userDetails?.role ?? null;
        const allowedRoles = ['admin', 'safety_manager', 'user_admin'];
        if (!role || !allowedRoles.includes(role)) {
          await supabase.auth.signOut();
          setError("Du har inte behörighet att logga in på adminpanelen.");
          setIsLoading(false);
          return undefined;
        }

        const authUser: AuthUser = {
          isAuth: true,
          token: session.access_token,
          user_id: user.id,
          group_id: userDetails?.group_id ?? null,
          organisation_id: userDetails?.organisation_id ?? null,
          role,
        };

        dispatch({
          type: "LOGIN",
          payload: authUser,
        });

        identifyUser({
          user_id: user.id,
          group_id: userDetails?.group_id ?? null,
          organisation_id: userDetails?.organisation_id ?? null,
        });

        localStorage.setItem(
          "userData",
          JSON.stringify({
            id: user.id,
            group_id: userDetails?.group_id ?? null,
            organisation_id: userDetails?.organisation_id ?? null,
            token: session.access_token,
            device_token: userDetails?.device_token ?? null,
            role,
          })
        );

        setIsLoading(false);
        return authUser;
      } catch (userDetailsError) {
        captureException(userDetailsError, {
          source: "useLogin",
          step: "fetchUserDetails",
          email,
        });
        await supabase.auth.signOut();
        setError("Kunde inte verifiera användarbehörighet.");
        setIsLoading(false);
        return undefined;
      }
    } catch (err) {
      captureException(err, { source: "useLogin", step: "verifyOTP", email });
      setError("Verifiering misslyckades");
      setIsLoading(false);
      return undefined;
    }
  };

  return { requestOTP, verifyOTP, error, isLoading, otpSent };
};
